import { Span } from '@opentelemetry/sdk-trace-web';

/**
 * Redacts sensitive query parameters from URL attributes on an OpenTelemetry span.
 *
 * Iterates over the given `urlKeys` and, for each one whose value is a string
 * containing a query string, replaces the values of any `sensitiveParams` with
 * `[REDACTED]` before writing the sanitized URL back via `span.setAttribute`.
 *
 * @see https://opentelemetry.io/docs/security/handling-sensitive-data/
 *
 * @param span - The OpenTelemetry SDK span whose attributes will be sanitized.
 * @param urlKeys - Span attribute keys that may contain a URL (e.g. `['http.url', 'url.full', 'http.target']`).
 * @param sensitiveParams - Query parameter names whose values should be redacted (e.g. `['addressLine']`).
 *
 * @example
 * // Redacts `addressLine` from all standard URL attributes
 * sanitizeUrlAttributes(span, ['http.url', 'url.full', 'http.target'], ['addressLine']);
 * // Before: https://example.com?addressLine=123+Main+St
 * // After:  https://example.com?addressLine=[REDACTED]
 */
export const sanitizeUrlAttributes = (span: Span, urlKeys: string[], sensitiveParams: string[]) => {
  if (!span.attributes) return;
  urlKeys.forEach((key) => {
    const rawUrl = span.attributes[key];
    if (typeof rawUrl === 'string' && rawUrl.includes('?')) {
      const pattern = new RegExp(
        `((?:${sensitiveParams.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})=)[^&]*`,
        'g',
      );
      const sanitized = rawUrl.replace(pattern, '$1[REDACTED]');
      span.setAttribute(key, sanitized);
    }
  });
};
